/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { NotAvailable } from '@/components/blocks/NotAvailable';
import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { ProductDetailsDisplay } from '@/components/blocks/ProductDetails/Display';
import { ProductDetailsAplazoDisplay } from '@/components/blocks/ProductDetails/Aplazo';
import {
	extractCodeFromPath,
	getImageUrl,
	IMAGE_DEFAULT_URL,
	ProductDetailsGallery,
} from '@/components/blocks/ProductDetails/Gallery';
import {
	ProductDetailsPrice,
	useProductDetailsPrice,
} from '@/components/blocks/ProductDetails/Price';
import { ProductDetailsPromos } from '@/components/blocks/ProductDetails/Promos';
import { ProductDetailsSeller } from '@/components/blocks/ProductDetails/Seller';
import {
	productDetailsBinaryElementSX,
	productDetailsImageBinaryElementSX,
	productDetailsImageBinaryElementMobileSX,
	productDetailsImageBinaryElementMobile2SX,
	productDetailsPromosElementMobile2SX,
} from '@/components/blocks/ProductDetails/styles/binaryElement';
import { productDetailsContainerSX } from '@/components/blocks/ProductDetails/styles/container';
import { ProductDetailsAttributes } from '@/components/content/ProductDetails/parts/Attributes';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { ContentProvider } from '@/data/context/content';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { ID } from '@/data/types/Basic';
import { Paper, Stack, Divider, useMediaQuery, Box, Slide } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CheckAvailabilityDisplay } from '@/components/blocks/ProductDetails/CheckAvailability';
import { ProductDetailsBenefitsDisplay } from '@/components/blocks/ProductDetails/Benefits';
import { ProductDescriptionDetails } from '@/components/blocks/ProductDetails/ProductDescriptionDetails';
import { ColorProduct } from '@/components/blocks/ProductDetails/ColorProduct';
import { CompleteLook } from '@/components/content/ProductDetails/parts/CompleteLook';
import {
	SpotBackToSchool,
	SpotBottomRight,
	SpotCarousels,
} from '@/components/content/ProductDetails/parts/SpotBottomRight';
import { ProductDetailsBenefitsMobileDisplay } from '@/components/blocks/ProductDetails/BenefitsMobile';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { useIsDpElectronico } from '@/data/Content/DpElectronicos';
import { ProductDetailsDrawer } from './parts/ProductDetailsDrawer';
import { ProductDetailsAvailability } from '@/components/blocks/ProductDetails/Availability';
import { usePriceBy } from '@/data/Content/Prices';
import { useCart } from '@/data/Content/Cart';
import { ProductDetailsSkeleton } from './parts/ProductDetailsSkeleton';
import { FreeShipping } from './parts/FreeShiping';
import { useStock } from '@/components/blocks/ProductDetails/hooks/useStock';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { ProductJsonLd } from '@/components/blocks/ProductJsonLd';
export interface InfoElectronico {
	hasOfferDeContado: boolean;
	prices: {
		offerDpVale: undefined;
		offerDeContado: string | undefined;
		strikedDeContado: string | undefined;
	};
}

export const ProductDetails: FC<{
	id: ID;
}> = ({ id }) => {
	const {
		dimensions: { contentSpacing },
	} = useTheme();
	const { data: cartDetails } = useCart();
	const { storeLocator } = useStoreLocatorState();
	const [physicalStoreName, setPhysicalStoreName] = useState<string>('');
	const productDetails = useProductDetails({ partNumber: id.toString(), physicalStoreName });
	const { product, selection, availability, onSelectionInitialElectronic } = productDetails;
	const { detailsNotAvailable } = useLocalization('productDetail');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { isDpElectronico, isLoading: isDpElectronicoLoading } = useIsDpElectronico(product?.id);

	const { data: dpElectronicosPrices, isLoading: isDpElectronicoPricesLoading } = usePriceBy(
		isDpElectronico ? { catalogEntryId: product?.id } : undefined
	);

	const infoElectronico = useMemo<InfoElectronico | undefined>(() => {
		if (!isDpElectronico || !dpElectronicosPrices || !product) {
			return undefined;
		}

		return {
			hasOfferDeContado: product['x_prices.field1'] ? true : false,
			prices: {
				offerDpVale: undefined,
				offerDeContado: dpElectronicosPrices.EntitledPrice?.at(0)
					?.UnitPrice?.at(0)
					?.price?.value?.toString(),
				strikedDeContado: product['x_prices.field1'],
			},
		};
	}, [product, dpElectronicosPrices, isDpElectronico]);

	const [isVisibleEnd, setIsVisibleEnd] = useState(false);
	const [isVisibleTop, setIsVisibleTop] = useState(false);
	const [isVisible, setIsVisible] = useState(false);

	const refButton = useRef<HTMLDivElement>(null);
	const refEnd = useRef<HTMLDivElement>(null);
	const refTop = useRef<HTMLDivElement>(null);

	const { price, priceWithoutOffer, shouldOffer } = calculateProductPrices({
		list: product?.productPrice.list ?? 0,
		offer: product?.productPrice.offer ?? 0,
		ribbons: selection?.sku?.ribbons ?? [],
	});

	const attributes = product?.attributes || [];

	useEffect(() => {
		setPhysicalStoreName(storeLocator.selectedStore?.physicalStoreName ?? '');
	}, [storeLocator.selectedStore]);

	useEffect(() => {
		if (infoElectronico) {
			onSelectionInitialElectronic();
		}
	}, [infoElectronico, onSelectionInitialElectronic]);

	useEffect(() => {
		const updatePosition = () => {
			if (refEnd.current) {
				const rect = refEnd.current.getBoundingClientRect();
				setIsVisibleEnd(rect.y < 70);
			}
		};

		updatePosition();
		window.addEventListener('scroll', updatePosition);

		return () => {
			window.removeEventListener('scroll', updatePosition);
		};
	}, []);

	useEffect(() => {
		if (isMobile) {
			const observerCallback = (entries: IntersectionObserverEntry[]) => {
				const entry = entries[0];
				setIsVisibleTop(entry.isIntersecting);
			};

			const setupObserver = () => {
				if (refTop.current) {
					const observer = new IntersectionObserver(observerCallback, { threshold: 0.1 });
					observer.observe(refTop.current);

					return () => observer.disconnect();
				} else {
					setTimeout(setupObserver, 100);
				}
			};

			setupObserver();
		}
	}, [isMobile]);
	useEffect(() => {
		if (isMobile) {
			const observerCallback = (entries: IntersectionObserverEntry[]) => {
				const entry = entries[0];
				setIsVisible(entry.isIntersecting);
			};

			const setupObserver = () => {
				if (refButton.current) {
					const observer = new IntersectionObserver(observerCallback, { threshold: 0.5 });
					observer.observe(refButton.current);

					return () => observer.disconnect();
				} else {
					setTimeout(setupObserver, 100);
				}
			};

			setupObserver();
		}
	}, [isMobile]);

	const shouldShowSlide = useMemo(
		() => !isVisibleTop && !isVisible && !isVisibleEnd,
		[isVisible, isVisibleEnd, isVisibleTop]
	);

	const { someWstock } = useStock(availability);

	const sku = selection?.sku || product;

	const productDetailsPrice = useProductDetailsPrice(product, sku);

	if (productDetails.loading || isDpElectronicoLoading || isDpElectronicoPricesLoading) {
		return <ProductDetailsSkeleton isMobile={isMobile} />;
	}

	if (!selection || !product) {
		return <NotAvailable message={detailsNotAvailable.t()} />;
	}

	return (
		<>
			<ContentProvider value={productDetails}>
				<Paper sx={productDetailsContainerSX({ isMobile })}>
					<Stack spacing={contentSpacing}>
						<Stack
							direction={{
								md: 'row',
							}}
							spacing={1}
							justifyContent="space-between"
						>
							{isMobile ? (
								<React.Fragment>
									<Box ref={refTop}>
										<Stack spacing={1} sx={productDetailsImageBinaryElementMobileSX}>
											<ProductDetailsDisplay />
											<ProductDetailsPrice
												infoElectronico={infoElectronico}
												orderId={cartDetails?.orderId}
											/>
										</Stack>
										<Stack spacing={1} sx={productDetailsImageBinaryElementSX}>
											<ProductDetailsGallery />
											<FreeShipping />
										</Stack>
										<Stack spacing={1} sx={productDetailsPromosElementMobile2SX}>
											<ProductDetailsPromos />
										</Stack>
									</Box>
									<Stack spacing={1} sx={productDetailsImageBinaryElementMobile2SX}>
										<Box ref={refButton}>
											<ProductDetailsAplazoDisplay />
											<Divider variant="middle" sx={{ my: '8px' }} />
											<Stack sx={{ marginBottom: '10px' }}>
												<ProductDetailsAvailability />
											</Stack>
											{!isDpElectronico ? <ColorProduct /> : null}
											{!isDpElectronico ? <ProductDetailsAttributes /> : null}
											<ProductDetailsSeller />

											<ProductDetailsAddToCart />
										</Box>
										{someWstock ? (
											<>
												{!isDpElectronico ? (
													<Slide direction="up" in={shouldShowSlide}>
														<Box
															sx={{
																padding: '5px',
																width: '100vw',
																position: 'fixed',
																bottom: '0px',
																left: '0px',
																zIndex: '1001',
																backgroundColor: 'white',
															}}
														>
															<ProductDetailsDrawer />
														</Box>
													</Slide>
												) : null}
											</>
										) : null}

										<CheckAvailabilityDisplay isDpElectronico={isDpElectronico} />
										<Divider variant="middle" />
										<ProductDetailsBenefitsMobileDisplay />
										<SpotBottomRight />

										<CompleteLook id={id} />

										<ProductDescriptionDetails isDpElectronico={isDpElectronico} />
										<Box ref={refEnd}></Box>
									</Stack>
								</React.Fragment>
							) : (
								<>
									<Stack spacing={1} sx={productDetailsImageBinaryElementSX}>
										<ProductDetailsGallery />
										<ProductDescriptionDetails isDpElectronico={isDpElectronico} />
									</Stack>
									<Stack spacing={0} sx={productDetailsBinaryElementSX}>
										<ProductDetailsDisplay isElectronic={isDpElectronico} />
										{product.type === 'bundle' ? null : (
											<Stack spacing={0}>
												<ProductDetailsPrice
													infoElectronico={infoElectronico}
													orderId={cartDetails?.orderId}
												/>
												<ProductDetailsAplazoDisplay />
												<ProductDetailsPromos />
												<Divider sx={{ my: '10px' }} />
												<Stack sx={{ marginBottom: '10px' }}>
													<ProductDetailsAvailability />
												</Stack>
												{!isDpElectronico ? <ColorProduct /> : null}
												{!isDpElectronico ? <ProductDetailsAttributes /> : null}
												<ProductDetailsSeller />
												<ProductDetailsAddToCart />
												<CheckAvailabilityDisplay isDpElectronico={isDpElectronico} />
												<Divider sx={{ my: '16px' }} />
												<ProductDetailsBenefitsDisplay />
												<SpotBottomRight />
												<ContentRecommendation
													id={'DPV2_WhatsAppFloating_Spot'}
													properties={{
														emsName: 'DPV2_WhatsAppFloating_Spot',
													}}
												/>
											</Stack>
										)}
									</Stack>
								</>
							)}
						</Stack>
					</Stack>
				</Paper>
				{!isMobile ? <CompleteLook id={id} /> : null}
				<SpotCarousels />
				<SpotBackToSchool />
			</ContentProvider>
			<ProductJsonLd
				sku={sku?.keyword.split(', ')[0]}
				brand={findProductAttribute(sku?.attributes ?? [], 'Marca')?.value}
				image={getImageUrl(IMAGE_DEFAULT_URL, extractCodeFromPath(sku?.thumbnail ?? '') ?? '', 1)}
				name={sku?.name}
				price={Number(infoElectronico?.prices.offerDeContado) || productDetailsPrice.price}
				inStock
			/>
		</>
	);
};
